// CustomerMenu.jsx — /menu?token=<qrToken>
// Route (NO auth guard): <Route path="/menu" element={<CustomerMenu />} />
// NO localStorage — all state from DB via token.

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { baseurl, imgurl } from '../../baseurl';
import {
  ShoppingCart, Plus, Minus, X, Clock,
  Loader2, MessageSquare, Phone, User, UtensilsCrossed,
  Search, AlertCircle, Send, Trash2, Timer, RefreshCw,
  ClipboardList, ChefHat, CheckCheck, AlertTriangle,
} from 'lucide-react';

const publicApi = () => axios.create({ baseURL: baseurl });

const STATUS = {
  cart:      { label: 'Queued',    emoji: '🛒', color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)' },
  placed:    { label: 'Received',  emoji: '⏳', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  kot:       { label: 'Kitchen',   emoji: '👨‍🍳', color: '#6366f1', bg: 'rgba(99,102,241,0.10)' },
  preparing: { label: 'Preparing', emoji: '🔥', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  ready:     { label: 'Ready!',    emoji: '✅', color: '#22c55e', bg: 'rgba(34,197,94,0.10)'  },
  served:    { label: 'Served',    emoji: '🍽️', color: '#9ca3af', bg: 'rgba(156,163,175,0.10)' },
};

// Show ALL non-cart statuses — including served
const SHOW_STATUSES = new Set(['placed', 'kot', 'preparing', 'ready']);

const parseItems = raw => {
  try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || []); }
  catch { return []; }
};

/* ── Local cart (in-memory, not yet sent to server) ── */
function useCart() {
  const [items, setItems] = useState([]);
  const add = useCallback((product, variant = 'full') => {
    const price  = variant === 'half' ? product.price_half : product.price_full;
    const cartId = `${product.id}_${variant}`;
    setItems(prev => {
      const ex = prev.find(i => i.cartId === cartId);
      if (ex) return prev.map(i => i.cartId === cartId ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { cartId, id: product.id, name: product.name, variant, price, qty: 1, note: '' }];
    });
  }, []);
  const updateQty = useCallback((cartId, delta) =>
    setItems(prev =>
      prev.map(i => i.cartId === cartId ? { ...i, qty: Math.max(0, i.qty + delta) } : i)
          .filter(i => i.qty > 0)), []);
  const remove  = useCallback(cartId => setItems(p => p.filter(i => i.cartId !== cartId)), []);
  const setNote = useCallback((cartId, note) => setItems(p => p.map(i => i.cartId === cartId ? { ...i, note } : i)), []);
  const clear   = () => setItems([]);
  const total   = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count   = items.reduce((s, i) => s + i.qty, 0);
  return { items, add, updateQty, remove, setNote, clear, total, count };
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
export default function CustomerMenu() {
  const qrToken = new URLSearchParams(window.location.search).get('token') || '';

  const [info,       setInfo]       = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems,  setMenuItems]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [activeCat,  setActiveCat]  = useState('all');
  const [search,     setSearch]     = useState('');
  const [screen,     setScreen]     = useState('browse');
  const [placing,    setPlacing]    = useState(false);

  /* ── Live table state — always from DB ── */
  const [tableData,       setTableData]       = useState(null);   // raw response
  const [sharedCartItems, setSharedCartItems] = useState([]);
  const [liveOrders,      setLiveOrders]      = useState([]);
  const [pollLoading,     setPollLoading]     = useState(false);
  const [tableOccupied,   setTableOccupied]   = useState(false);  // sitting_active from DB

  /* ── Customer details — from DB, editable ── */
  const [customerName,  setCustomerName]  = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [orderNote,     setOrderNote]     = useState('');

  // Track manual edits so poll doesn't overwrite while typing
  const userEditedName  = useRef(false);
  const userEditedPhone = useRef(false);
  // On first poll after page load — auto-navigate to myorders if table has active data
  const firstPollDone   = useRef(false);

  const cart       = useCart();
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Shared: apply table-orders API response to state
  const applyTableData = useCallback((data) => {
    setTableData(data);
    if (data.table_available) {
      setTableOccupied(false);
      setSharedCartItems([]);
      setLiveOrders([]);
      if (!userEditedName.current)  setCustomerName('');
      if (!userEditedPhone.current) setCustomerPhone('');
      return;
    }
    setTableOccupied(true);
    setLiveOrders((data.orders || []).filter(o => SHOW_STATUSES.has(o.status)));
    setSharedCartItems(data.cart_items || []);
    if (data.customer_name  && !userEditedName.current)  setCustomerName(data.customer_name);
    if (data.customer_phone && !userEditedPhone.current) setCustomerPhone(data.customer_phone);
  }, []); // eslint-disable-line

  // On mount: load menu + table state together, then pick initial screen
  useEffect(() => {
    if (!qrToken) { setError('Invalid QR code - token missing.'); setLoading(false); return; }
    initApp();
  }, []); // eslint-disable-line

  const initApp = async () => {
    setLoading(true);
    try {
      const [{ data: resolved }, { data: menu }, { data: tableResp }] = await Promise.all([
        publicApi().get(`/public/resolve?token=${qrToken}`),
        publicApi().get(`/public/menu?token=${qrToken}`),
        publicApi().get(`/public/table-orders?token=${qrToken}`),
      ]);

      setInfo(resolved);
      setCategories(menu.categories || []);
      setMenuItems(
        (menu.items || [])
          .map(p => ({
            ...p,
            tags:           Array.isArray(p.tags) ? p.tags : (p.tags ? JSON.parse(p.tags) : []),
            is_available:   p.is_available !== undefined ? Boolean(p.is_available) : true,
            has_half_plate: Boolean(p.has_half_plate),
            price_full:     parseFloat(p.price_full ?? p.price ?? 0),
            price_half:     p.price_half ? parseFloat(p.price_half) : null,
          }))
          .filter(p => p.is_available)
      );

      applyTableData(tableResp);

      // Pick initial screen AFTER all data is set, before loading=false renders anything
      if (!tableResp.table_available) {
        const confirmed = (tableResp.orders || []).filter(o => SHOW_STATUSES.has(o.status));
        if (confirmed.length > 0 || (tableResp.cart_items || []).length > 0) {
          setScreen('myorders');
        }
      }

      firstPollDone.current = true;
    } catch (e) {
      setError(
        e.response?.status === 404
          ? 'This QR code is no longer valid. Please ask staff for assistance.'
          : (e.response?.data?.message || 'Failed to load menu. Please scan the QR code again.')
      );
    } finally { setLoading(false); }
  };

  /* ── Poll table orders from DB ─────────────────────────────────────────
     Logic:
     - table_available: true  → table is free, no active sitting → show empty new order UI
     - sitting_active: true   → table occupied → show all orders + name/phone from DB
     - orders array contains ALL statuses including 'served' — show them all
     - cart_items = status='cart' rows (POS-staged items)
     - customer_name/phone from DB → auto-fill fields if user hasn't typed yet
  ─────────────────────────────────────────────────────────────────────── */
  // Background poll every 12s — just refreshes state, never changes screen
  const pollOrders = useCallback(async () => {
    if (!qrToken || !mountedRef.current) return;
    setPollLoading(true);
    try {
      const { data } = await publicApi().get(`/public/table-orders?token=${qrToken}`);
      if (!mountedRef.current) return;
      applyTableData(data);
    } catch (_) { /* keep last known state on network error */ }
    finally { if (mountedRef.current) setPollLoading(false); }
  }, [qrToken, applyTableData]);

  // Poll every 12s — init already fetched on mount, so just start the interval
  useEffect(() => {
    if (!qrToken) return;
    const id = setInterval(pollOrders, 12000);
    return () => clearInterval(id);
  }, [qrToken, pollOrders]);

  /* ── Derived state ── */
  const filtered = useMemo(() =>
    menuItems.filter(p =>
      (activeCat === 'all' || p.category === activeCat || String(p.category_id) === activeCat) &&
      p.name.toLowerCase().includes(search.toLowerCase())
    ), [menuItems, activeCat, search]);

  const localCartTotal  = cart.total;
  const sharedCartTotal = sharedCartItems.reduce((s, i) => s + parseFloat(i.price || 0) * parseInt(i.qty || 0), 0);
  const liveOrdersTotal = liveOrders.reduce((s, o) => s + parseFloat(o.grand_total || 0), 0);
  const hasSharedCart   = sharedCartItems.length > 0;
  const hasLiveOrders   = liveOrders.length > 0;
  const hasAnything     = hasSharedCart || hasLiveOrders;

  // Merge all items across all confirmed orders into one flat list
  const allConfirmedItems = useMemo(() => {
    const map = {};
    liveOrders.forEach(order => {
      parseItems(order.items).forEach(it => {
        const key = `${it.name}__${it.variant || 'full'}`;
        if (map[key]) map[key].qty += parseInt(it.qty || 1);
        else          map[key] = { ...it, qty: parseInt(it.qty || 1) };
        map[key].lineTotal = +((map[key].price || 0) * map[key].qty).toFixed(2);
      });
    });
    return Object.values(map);
  }, [liveOrders]);

  /* ── Place order ── */
  const handleSendToTable = async () => {
  if (!customerName.trim())  { alert('Please enter your name');         return; }
  if (!customerPhone.trim()) { alert('Please enter your phone number'); return; }
  if (!cart.items.length)    { alert('Add items to cart first');        return; }

  setPlacing(true);
  try {
    await publicApi().post('/public/orders', {
      qr_token:       qrToken,
      customer_name:  customerName.trim(),
      customer_phone: customerPhone.trim(),
      order_note:     orderNote.trim() || null,
      gst_rate:       0,
      gst_type:       'exclusive',
      items: cart.items.map(i => ({
        menu_item_id: i.id,
        name:         i.name,
        variant:      i.variant,
        price:        i.price,
        qty:          i.qty,
        total:        +(i.price * i.qty).toFixed(2),
        note:         i.note || null,
      })),
    });

    cart.clear();
    setOrderNote('');

    // Immediately re-fetch to show the merged cart (POS + QR items together)
    await pollOrders();
    setScreen('myorders');

  } catch (e) {
    alert(e.response?.data?.message || 'Failed to send items. Please try again.');
  } finally {
    setPlacing(false);
  }
};

  /* ════════════════════════════════════════
     LOADING / ERROR
  ════════════════════════════════════════ */
  if (loading) return (
    <Splash>
      <Loader2 size={30} style={{ animation:'spin 0.8s linear infinite', color:'#ff8c00' }}/>
      <p style={{ marginTop:12, color:'#8c7355', fontSize:13 }}>Loading menu…</p>
    </Splash>
  );

  if (error) return (
    <Splash>
      <AlertCircle size={40} style={{ color:'#ef4444' }}/>
      <h3 style={{ fontWeight:800, marginTop:10, color:'#1a1208' }}>Oops!</h3>
      <p style={{ color:'#8c7355', textAlign:'center', maxWidth:260, lineHeight:1.5, fontSize:13 }}>{error}</p>
    </Splash>
  );

  /* ════════════════════════════════════════
     MY ORDERS SCREEN
     Shows all active orders + cart queue
  ════════════════════════════════════════ */
  if (screen === 'myorders') return (
    <Page title={`Table ${info?.table_label}`} name={info?.restaurant_name}
      right={
        <button onClick={pollOrders} style={S.ghost}>
          {pollLoading ? <Loader2 size={13} style={{ animation:'spin 0.7s linear infinite' }}/> : <RefreshCw size={13}/>}
        </button>
      }>
      <div style={{ padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>

        {/* POS-staged queue (status='cart') */}
        {hasSharedCart && (
          <div style={cardStyle('rgba(255,140,0,0.25)')}>
            <div style={cardHeader('rgba(255,140,0,0.09)')}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <ShoppingCart size={13} style={{ color:'#ff8c00' }}/>
                <span style={{ fontSize:11, fontWeight:800, color:'#b45309' }}>Order Queue</span>
                <span style={{ fontSize:10, color:'#d97706' }}>· awaiting staff</span>
              </div>
              <span style={{ fontSize:12, fontWeight:800, color:'#ff8c00' }}>₹{sharedCartTotal.toFixed(0)}</span>
            </div>
            <div style={{ padding:'10px 14px' }}>
              {sharedCartItems.map((it, i) => (
                <ItemRow key={i} item={it} isLast={i === sharedCartItems.length - 1}/>
              ))}
              <Subtotal label="Queue Total" amount={sharedCartTotal}/>
              <div style={{ marginTop:6, background:'rgba(245,158,11,0.08)', borderRadius:8, padding:'7px 10px', display:'flex', alignItems:'center', gap:6 }}>
                <AlertTriangle size={11} style={{ color:'#d97706', flexShrink:0 }}/>
                <span style={{ fontSize:10, color:'#92400e', lineHeight:1.4 }}>GST &amp; final bill applied by staff · Pay at table</span>
              </div>
            </div>
          </div>
        )}

        {/* Confirmed orders (placed / kot / preparing / ready / served) */}
        {hasLiveOrders && (
          <div style={cardStyle('rgba(34,197,94,0.20)')}>
            <div style={cardHeader('rgba(34,197,94,0.07)')}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <CheckCheck size={13} style={{ color:'#16a34a' }}/>
                <span style={{ fontSize:11, fontWeight:800, color:'#166534' }}>Your Orders</span>
                <span style={{ fontSize:10, color:'#16a34a' }}>· {liveOrders.length} order{liveOrders.length !== 1 ? 's' : ''}</span>
              </div>
              <span style={{ fontSize:12, fontWeight:800, color:'#16a34a' }}>₹{liveOrdersTotal.toFixed(0)}</span>
            </div>
            <div style={{ padding:'10px 14px' }}>
              {liveOrders.map(o => <OrderStatusCard key={o.id} order={o} expanded/>)}

              {/* Merged items summary */}
              {allConfirmedItems.length > 0 && (
                <div style={{ marginTop:8, paddingTop:8, borderTop:'1px dashed rgba(0,0,0,0.08)' }}>
                  <p style={{ fontSize:10, fontWeight:700, color:'#8c7355', margin:'0 0 8px' }}>ALL ITEMS ORDERED</p>
                  {allConfirmedItems.map((it, i) => (
                    <ItemRow key={i} item={it} isLast={i === allConfirmedItems.length - 1}/>
                  ))}
                  <p style={{ fontSize:10, color:'#c4b49e', margin:'6px 0 0' }}>+ GST &amp; charges at checkout · Pay at table</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Running total */}
        {hasAnything && (
          <div style={{ background:'#fff', borderRadius:14, padding:'12px 14px', border:'1.5px solid rgba(0,0,0,0.06)' }}>
            {hasLiveOrders && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#8c7355', marginBottom:6 }}>
                <span>Ordered total</span><span>₹{liveOrdersTotal.toFixed(0)}</span>
              </div>
            )}
            {hasSharedCart && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#8c7355', marginBottom:6 }}>
                <span>In queue</span><span>₹{sharedCartTotal.toFixed(0)}</span>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, fontWeight:800, color:'#1a1208', borderTop:'1px dashed rgba(0,0,0,0.08)', paddingTop:8 }}>
              <span>Running Total</span>
              <span style={{ color:'#ff8c00' }}>₹{(sharedCartTotal + liveOrdersTotal).toFixed(0)}</span>
            </div>
            <p style={{ fontSize:10, color:'#c4b49e', margin:'4px 0 0' }}>Excludes GST — final bill at checkout</p>
          </div>
        )}

        {!hasAnything && (
          <div style={{ textAlign:'center', padding:'40px 0', color:'#8c7355', display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
            <ShoppingCart size={40} style={{ opacity:0.2 }}/>
            <p style={{ fontSize:13 }}>No active orders for this table</p>
          </div>
        )}

        <button onClick={() => setScreen('browse')} style={{ ...S.primary, marginTop:4 }}>
          <Plus size={14}/> Add More Items
        </button>
      </div>
    </Page>
  );

  /* ════════════════════════════════════════
     CHECKOUT SCREEN
  ════════════════════════════════════════ */
  if (screen === 'checkout') return (
    <Page title="Send to Table" back={() => setScreen('cart')} backLabel="Cart">
      <div style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:12 }}>

        {/* Context — table already has orders */}
        {hasAnything && (
          <div style={{ background:'rgba(255,140,0,0.07)', border:'1px solid rgba(255,140,0,0.18)', borderRadius:10, padding:'10px 12px', fontSize:11, color:'#92400e', lineHeight:1.6 }}>
            <strong>Adding to your table's active order</strong>
            {hasSharedCart && ` · ${sharedCartItems.length} item${sharedCartItems.length !== 1 ? 's' : ''} in queue`}
            {hasLiveOrders && ` · ${liveOrders.length} order${liveOrders.length !== 1 ? 's' : ''} confirmed`}
          </div>
        )}

        <p style={{ fontSize:12, color:'#8c7355', margin:0 }}>Your details so staff knows who to serve</p>

        {/* Name auto-filled from DB if table occupied */}
        <Field icon={<User size={14}/>}
          placeholder="Your name *"
          value={customerName}
          onChange={v => { userEditedName.current = true; setCustomerName(v); }}/>

        <Field icon={<Phone size={14}/>}
          placeholder="Phone number *"
          value={customerPhone} type="tel"
          onChange={v => { userEditedPhone.current = true; setCustomerPhone(v); }}/>

        <Field icon={<MessageSquare size={14}/>}
          placeholder="Special instructions (optional)"
          value={orderNote}
          onChange={setOrderNote}/>

        {/* Items being added */}
        <div style={{ background:'#fff', borderRadius:12, padding:'12px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
          <p style={{ fontSize:11, fontWeight:700, color:'#8c7355', marginBottom:8 }}>
            ITEMS YOU'RE ADDING · Table {info?.table_label}
          </p>
          {cart.items.map(item => (
            <div key={item.cartId} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:5 }}>
              <span style={{ fontWeight:600 }}>
                {item.name}{item.variant === 'half' ? ' ½' : ''} ×{item.qty}
                {item.note ? <span style={{ color:'#8c7355', fontStyle:'italic', marginLeft:5 }}>· {item.note}</span> : null}
              </span>
              <span style={{ fontWeight:700, color:'#ff8c00' }}>₹{(item.price * item.qty).toFixed(0)}</span>
            </div>
          ))}
          <div style={{ borderTop:'1px dashed #e5e0d8', marginTop:8, paddingTop:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, fontWeight:800, color:'#1a1208' }}>
              <span>Items Total</span>
              <span style={{ color:'#ff8c00' }}>₹{localCartTotal.toFixed(0)}</span>
            </div>
            <p style={{ fontSize:10, color:'#c4b49e', marginTop:4 }}>GST applied by staff at checkout · Pay at table</p>
          </div>
        </div>

        {/* Already ordered on this table */}
        {hasAnything && (
          <div style={{ background:'rgba(34,197,94,0.04)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:10, padding:'10px 12px' }}>
            <p style={{ fontSize:10, fontWeight:700, color:'#166534', margin:'0 0 6px' }}>ALREADY ON THIS TABLE</p>
            {hasSharedCart && <p style={{ fontSize:11, color:'#8c7355', margin:'0 0 2px' }}>Queue: {sharedCartItems.length} item{sharedCartItems.length !== 1 ? 's' : ''} · ₹{sharedCartTotal.toFixed(0)}</p>}
            {hasLiveOrders && <p style={{ fontSize:11, color:'#8c7355', margin:0 }}>Ordered: {allConfirmedItems.reduce((s, i) => s + i.qty, 0)} items · ₹{liveOrdersTotal.toFixed(0)}</p>}
          </div>
        )}

        <button onClick={handleSendToTable} disabled={placing} style={{ ...S.primary, opacity: placing ? 0.7 : 1 }}>
          {placing
            ? <><Loader2 size={15} style={{ animation:'spin 0.7s linear infinite' }}/> Sending…</>
            : <><Send size={14}/> Send to Table</>}
        </button>
      </div>
    </Page>
  );

  /* ════════════════════════════════════════
     CART SCREEN
  ════════════════════════════════════════ */
  if (screen === 'cart') return (
    <Page title={`Cart · Table ${info?.table_label}`} back={() => setScreen('browse')} backLabel="Menu">
      <div style={{ padding:'12px 14px' }}>
        {cart.items.length === 0 ? (
          <div style={{ ...S.center, minHeight:200 }}>
            <ShoppingCart size={36} style={{ opacity:0.3 }}/>
            <p style={{ color:'#8c7355', marginTop:8, fontSize:13 }}>Your cart is empty</p>
            <button onClick={() => setScreen('browse')} style={{ ...S.primary, marginTop:12, width:'auto', padding:'9px 20px' }}>Browse Menu</button>
          </div>
        ) : (
          <>
            {cart.items.map(item => (
              <CartItemRow key={item.cartId} item={item}
                onInc={()  => cart.updateQty(item.cartId, +1)}
                onDec={()  => cart.updateQty(item.cartId, -1)}
                onRemove={() => cart.remove(item.cartId)}
                onNote={n  => cart.setNote(item.cartId, n)}/>
            ))}

            <div style={{ background:'#fff', borderRadius:12, padding:'12px 14px', marginTop:4, boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, fontWeight:800, color:'#1a1208', marginBottom:4 }}>
                <span>Items Total</span>
                <span style={{ color:'#ff8c00' }}>₹{localCartTotal.toFixed(0)}</span>
              </div>
              <p style={{ fontSize:10, color:'#c4b49e', margin:0 }}>GST applied by staff at checkout · Pay at table</p>
            </div>

            {/* Show existing table orders inside cart */}
            {hasAnything && (
              <div style={{ marginTop:10, borderRadius:12, overflow:'hidden', border:'1px solid rgba(255,140,0,0.20)' }}>
                <button onClick={() => setScreen('myorders')}
                  style={{ width:'100%', background:'rgba(255,140,0,0.06)', border:'none', padding:'9px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'#92400e', display:'flex', alignItems:'center', gap:5 }}>
                    <ClipboardList size={12} style={{ color:'#ff8c00' }}/> Active table orders
                  </span>
                  <span style={{ fontSize:11, color:'#ff8c00', fontWeight:700 }}>View all →</span>
                </button>

                {hasSharedCart && (
                  <div style={{ padding:'8px 14px', borderTop:'1px solid rgba(255,140,0,0.12)', background:'#fff' }}>
                    <p style={{ fontSize:10, fontWeight:700, color:'#b45309', margin:'0 0 5px', display:'flex', alignItems:'center', gap:4 }}>
                      <ShoppingCart size={10}/> IN QUEUE · ₹{sharedCartTotal.toFixed(0)}
                    </p>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                      {sharedCartItems.map((it, i) => (
                        <span key={i} style={{ fontSize:10, background:'rgba(255,140,0,0.10)', border:'1px solid rgba(255,140,0,0.20)', borderRadius:6, padding:'2px 8px', color:'#92400e' }}>
                          {it.name}{it.variant === 'half' ? ' ½' : ''} ×{it.qty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {hasLiveOrders && allConfirmedItems.length > 0 && (
                  <div style={{ padding:'8px 14px', borderTop:'1px solid rgba(34,197,94,0.10)', background:'#fff' }}>
                    <p style={{ fontSize:10, fontWeight:700, color:'#166534', margin:'0 0 5px', display:'flex', alignItems:'center', gap:4 }}>
                      <CheckCheck size={10}/> ORDERED · ₹{liveOrdersTotal.toFixed(0)}
                    </p>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                      {allConfirmedItems.map((it, i) => (
                        <span key={i} style={{ fontSize:10, background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.18)', borderRadius:6, padding:'2px 8px', color:'#166534' }}>
                          {it.name}{it.variant === 'half' ? ' ½' : ''} ×{it.qty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ padding:'8px 14px', borderTop:'1px solid rgba(0,0,0,0.06)', background:'rgba(0,0,0,0.01)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'#8c7355' }}>Table running total</span>
                  <span style={{ fontSize:13, fontWeight:800, color:'#1a1208' }}>₹{(sharedCartTotal + liveOrdersTotal).toFixed(0)}</span>
                </div>
              </div>
            )}

            <button onClick={() => setScreen('checkout')} style={{ ...S.primary, marginTop:12 }}>
              <Send size={15}/> Review &amp; Send to Table
            </button>
          </>
        )}
      </div>
    </Page>
  );

  /* ════════════════════════════════════════
     BROWSE SCREEN
  ════════════════════════════════════════ */
  const totalQueuedQty    = sharedCartItems.reduce((s, i) => s + parseInt(i.qty || 0), 0);
  const totalConfirmedQty = allConfirmedItems.reduce((s, i) => s + i.qty, 0);

  return (
    <div style={{ background:'#faf7f2', minHeight:'100vh', paddingBottom: cart.count > 0 ? 88 : 24 }}>

      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#ff8c00,#ffbf00)', padding:'18px 16px 14px', color:'#fff' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          {info?.restaurant_logo
            ? <img src={info.restaurant_logo} alt="" style={{ width:32, height:32, borderRadius:8, objectFit:'cover', border:'2px solid rgba(255,255,255,0.4)' }}/>
            : <UtensilsCrossed size={20}/>}
          <div>
            <h1 style={{ fontSize:16, fontWeight:900, lineHeight:1, margin:0 }}>{info?.restaurant_name || 'Menu'}</h1>
            <p style={{ fontSize:11, opacity:0.85, margin:'2px 0 0' }}>Table {info?.table_label} · Scan &amp; Order</p>
          </div>
          {/* Always show orders button if table occupied */}
          {(hasAnything || tableOccupied) && (
            <button onClick={() => setScreen('myorders')}
              style={{ marginLeft:'auto', background:'rgba(255,255,255,0.25)', border:'none', borderRadius:8, padding:'5px 10px', color:'#fff', fontSize:10, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
              <ClipboardList size={11}/>
              {hasSharedCart ? `Queue (${totalQueuedQty})` : hasLiveOrders ? `Orders (${totalConfirmedQty})` : 'My Orders'}
            </button>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(255,255,255,0.22)', borderRadius:10, padding:'8px 12px' }}>
          <Search size={13} style={{ opacity:0.8 }}/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search dishes…"
            style={{ border:'none', background:'transparent', outline:'none', color:'#fff', fontSize:13, flex:1 }}/>
          {search && <button onClick={() => setSearch('')} style={{ border:'none', background:'none', cursor:'pointer', color:'rgba(255,255,255,0.8)', padding:0, display:'flex' }}><X size={12}/></button>}
        </div>
      </div>

      {/* Category tabs */}
      <div style={{ display:'flex', gap:6, padding:'10px 14px 8px', overflowX:'auto', background:'#fff', borderBottom:'1px solid rgba(0,0,0,0.06)' }}>
        {[{ id:'all', name:'All', icon:'🍽️' }, ...categories].map(c => {
          const active = c.id === 'all' ? activeCat === 'all' : activeCat === String(c.id);
          return (
            <button key={c.id} onClick={() => setActiveCat(c.id === 'all' ? 'all' : String(c.id))}
              style={{ flexShrink:0, display:'flex', alignItems:'center', gap:4, padding:'5px 12px', borderRadius:100, border:'1.5px solid', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
                background: active ? '#ff8c00' : '#fff', borderColor: active ? '#ff8c00' : 'rgba(0,0,0,0.09)', color: active ? '#fff' : '#8c7355' }}>
              {c.icon && <span>{c.icon}</span>}{c.name}
            </button>
          );
        })}
      </div>

      {/* Active orders banner */}
      {hasAnything && (
        <button onClick={() => setScreen('myorders')}
          style={{ width:'100%', background:'rgba(255,140,0,0.07)', border:'none', borderBottom:'1px solid rgba(255,140,0,0.15)', padding:'9px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
          <span style={{ fontSize:11, color:'#92400e', fontWeight:600, display:'flex', alignItems:'center', gap:5 }}>
            <ClipboardList size={12} style={{ color:'#ff8c00' }}/>
            {hasSharedCart && `${totalQueuedQty} item${totalQueuedQty !== 1 ? 's' : ''} in queue`}
            {hasSharedCart && hasLiveOrders && ' · '}
            {hasLiveOrders && `${totalConfirmedQty} item${totalConfirmedQty !== 1 ? 's' : ''} ordered`}
            {` · ₹${(sharedCartTotal + liveOrdersTotal).toFixed(0)}`}
          </span>
          <span style={{ fontSize:11, fontWeight:800, color:'#ff8c00' }}>View →</span>
        </button>
      )}

      {/* POS-staged items banner */}
      {hasSharedCart && (
        <div style={{ background:'rgba(139,92,246,0.07)', borderBottom:'1px solid rgba(139,92,246,0.15)', padding:'8px 14px', display:'flex', alignItems:'center', gap:6 }}>
          <ChefHat size={12} style={{ color:'#8b5cf6', flexShrink:0 }}/>
          <span style={{ fontSize:11, color:'#7c3aed', fontWeight:600 }}>
            {totalQueuedQty} item{totalQueuedQty !== 1 ? 's' : ''} added by staff for Table {info?.table_label}
          </span>
          <button onClick={pollOrders} style={{ marginLeft:'auto', border:'none', background:'none', cursor:'pointer', color:'#8b5cf6', display:'flex', padding:2 }}>
            {pollLoading ? <Loader2 size={11} style={{ animation:'spin 0.7s linear infinite' }}/> : <RefreshCw size={11}/>}
          </button>
        </div>
      )}

      {/* Menu items */}
      <div style={{ padding:'10px 14px', display:'flex', flexDirection:'column', gap:8 }}>
        {filtered.length === 0
          ? <div style={{ ...S.center, minHeight:160 }}><span style={{ fontSize:34 }}>🍽️</span><p style={{ color:'#8c7355', marginTop:8 }}>No items found</p></div>
          : filtered.map(p => {
              const inQueue = sharedCartItems
                .filter(i => String(i.menu_item_id) === String(p.id) || i.name === p.name)
                .reduce((s, i) => s + parseInt(i.qty || 0), 0);
              const inConfirmed = allConfirmedItems
                .filter(i => i.name === p.name)
                .reduce((s, i) => s + i.qty, 0);
              return (
                <MenuItemCard key={p.id} product={p}
                  localFull={cart.items.find(i => i.cartId === `${p.id}_full`)?.qty  || 0}
                  localHalf={cart.items.find(i => i.cartId === `${p.id}_half`)?.qty || 0}
                  inQueue={inQueue}
                  inConfirmed={inConfirmed}
                  onAdd={v => cart.add(p, v)}/>
              );
            })
        }
      </div>

      {/* Floating cart button */}
      {cart.count > 0 && (
        <div style={{ position:'fixed', bottom:16, left:'50%', transform:'translateX(-50%)', zIndex:100, width:'calc(100% - 28px)', maxWidth:420 }}>
          <button onClick={() => setScreen('cart')}
            style={{ ...S.primary, boxShadow:'0 8px 28px rgba(255,140,0,0.38)', justifyContent:'space-between' }}>
            <span style={{ display:'flex', alignItems:'center', gap:8 }}>
              <ShoppingCart size={16}/>{cart.count} new item{cart.count !== 1 ? 's' : ''}
            </span>
            <strong>₹{localCartTotal.toFixed(0)} →</strong>
          </button>
        </div>
      )}
      <KF/>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════════════════ */

function MenuItemCard({ product, localFull, localHalf, inQueue, inConfirmed, onAdd }) {
  const imgSrc  = product.image_url ? `${imgurl}${product.image_url}` : null;
  const inLocal = localFull + localHalf;
  const badgeCount = inLocal > 0 ? inLocal : inQueue > 0 ? inQueue : inConfirmed > 0 ? inConfirmed : 0;
  const badgeBg    = inLocal > 0 ? '#ff8c00' : inQueue > 0 ? 'rgba(255,140,0,0.70)' : inConfirmed > 0 ? '#22c55e' : null;
  const borderCol  = inLocal > 0 ? 'rgba(255,140,0,0.35)' : inQueue > 0 ? 'rgba(255,140,0,0.20)' : inConfirmed > 0 ? 'rgba(34,197,94,0.20)' : 'rgba(0,0,0,0.06)';
  return (
    <div style={{ background:'#fff', borderRadius:14, overflow:'hidden', display:'flex', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', border:`1.5px solid ${borderCol}`, position:'relative' }}>
      {badgeCount > 0 && badgeBg && (
        <div style={{ position:'absolute', top:8, left:8, background:badgeBg, color:'#fff', borderRadius:100, width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, zIndex:2 }}>
          {badgeCount}
        </div>
      )}
      {imgSrc && (
        <div style={{ width:88, height:88, flexShrink:0 }}>
          <img src={imgSrc} alt={product.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => e.target.parentElement.style.display='none'}/>
        </div>
      )}
      <div style={{ flex:1, padding:'10px 12px', display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#1a1208', lineHeight:1.3 }}>
            {product.tags?.includes('Popular') && <span style={{ marginRight:4 }}>🔥</span>}{product.name}
          </div>
          {product.description && <p style={{ fontSize:11, color:'#8c7355', marginTop:2, lineHeight:1.4, marginBottom:0 }}>{product.description.slice(0,70)}{product.description.length > 70 ? '…' : ''}</p>}
          {product.prep_time && <span style={{ fontSize:9, color:'#c4b49e', display:'flex', alignItems:'center', gap:2, marginTop:2 }}><Clock size={9}/>{product.prep_time}</span>}
          {inConfirmed > 0 && inLocal === 0 && (
            <span style={{ fontSize:9, color:'#16a34a', fontWeight:700, marginTop:2, display:'block' }}>✓ {inConfirmed} ordered</span>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:8 }}>
          <div>
            <span style={{ fontSize:13, fontWeight:800, color:'#ff8c00' }}>₹{product.price_full.toFixed(0)}</span>
            {product.has_half_plate && product.price_half && <span style={{ fontSize:10, color:'#8c7355', marginLeft:6 }}>½ ₹{product.price_half.toFixed(0)}</span>}
          </div>
          {product.has_half_plate && product.price_half ? (
            <div style={{ display:'flex', gap:4 }}>
              <button onClick={() => onAdd('half')} style={{ padding:'5px 8px', borderRadius:7, border:'1px solid rgba(255,191,0,0.40)', background:'rgba(255,191,0,0.10)', fontSize:10, fontWeight:700, cursor:'pointer', color:'#b87200' }}>½</button>
              <button onClick={() => onAdd('full')} style={{ padding:'5px 10px', borderRadius:7, border:'none', background:'#ff8c00', fontSize:10, fontWeight:700, cursor:'pointer', color:'#fff' }}>Full</button>
            </div>
          ) : (
            <button onClick={() => onAdd('full')} style={{ padding:'7px 14px', borderRadius:8, border:'none', background:'#ff8c00', fontSize:12, fontWeight:700, cursor:'pointer', color:'#fff', display:'flex', alignItems:'center', gap:4 }}>
              <Plus size={12}/> Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CartItemRow({ item, onInc, onDec, onRemove, onNote }) {
  const [showNote, setShowNote] = useState(false);
  return (
    <div style={{ background:'#fff', borderRadius:12, padding:'10px 12px', marginBottom:8, boxShadow:'0 2px 6px rgba(0,0,0,0.04)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
        <span style={{ fontSize:13, fontWeight:700, color:'#1a1208', flex:1 }}>{item.name}{item.variant === 'half' && <HalfBadge/>}</span>
        <span style={{ fontSize:13, fontWeight:800, color:'#ff8c00' }}>₹{(item.price * item.qty).toFixed(0)}</span>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(255,140,0,0.08)', borderRadius:8, padding:'4px 10px' }}>
          <button onClick={onDec} style={{ border:'none', background:'none', cursor:'pointer', padding:0, color:'#ff8c00', display:'flex' }}><Minus size={14}/></button>
          <span style={{ fontSize:13, fontWeight:800, minWidth:18, textAlign:'center' }}>{item.qty}</span>
          <button onClick={onInc} style={{ border:'none', background:'none', cursor:'pointer', padding:0, color:'#ff8c00', display:'flex' }}><Plus size={14}/></button>
        </div>
        <button onClick={() => setShowNote(v => !v)} style={{ border:'1px solid rgba(0,0,0,0.09)', borderRadius:7, padding:'4px 9px', background:'#fff', cursor:'pointer', fontSize:10, color: item.note ? '#ff8c00' : '#8c7355', fontWeight:600, display:'flex', alignItems:'center', gap:3 }}>
          <MessageSquare size={10}/> Note
        </button>
        <button onClick={onRemove} style={{ marginLeft:'auto', border:'none', background:'rgba(239,68,68,0.09)', borderRadius:7, padding:'5px 8px', cursor:'pointer', display:'flex', color:'#ef4444' }}>
          <Trash2 size={12}/>
        </button>
      </div>
      {showNote && <input autoFocus placeholder="e.g. no onion, less spicy…" value={item.note} onChange={e => onNote(e.target.value)} style={{ width:'100%', marginTop:8, border:'1px dashed rgba(255,140,0,0.28)', borderRadius:7, padding:'6px 10px', fontSize:11, outline:'none', background:'#fffaf4', boxSizing:'border-box' }}/>}
    </div>
  );
}

function OrderStatusCard({ order, expanded }) {
  const sm    = STATUS[order.status] || STATUS.placed;
  const items = useMemo(() => parseItems(order.items), [order.items]);
  const mins  = Math.floor((Date.now() - new Date(order.created_at)) / 60000);
  return (
    <div style={{ background:'rgba(0,0,0,0.02)', borderRadius:10, padding:'9px 12px', marginBottom:8, border:`1px solid ${sm.bg}` }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, fontWeight:800, color:'#1a1208' }}>#{order.order_number}</span>
          <span style={{ fontSize:10, fontWeight:700, color:sm.color, background:sm.bg, padding:'2px 8px', borderRadius:100 }}>{sm.emoji} {sm.label}</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:10, color: mins > 25 ? '#ef4444' : '#8c7355', display:'flex', alignItems:'center', gap:2 }}><Timer size={9}/> {mins}m</span>
          <span style={{ fontSize:12, fontWeight:800, color:'#ff8c00' }}>₹{parseFloat(order.grand_total || 0).toFixed(0)}</span>
        </div>
      </div>
      {expanded && items.length > 0 && (
        <div style={{ marginTop:8, display:'flex', flexWrap:'wrap', gap:4 }}>
          {items.map((it, i) => (
            <span key={i} style={{ fontSize:9.5, background:'rgba(0,0,0,0.05)', borderRadius:5, padding:'2px 7px', color:'#1a1208' }}>
              {it.name}{it.variant === 'half' ? ' ½' : ''} ×{it.qty}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemRow({ item, isLast }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12, marginBottom: isLast ? 0 : 8, paddingBottom: isLast ? 0 : 8, borderBottom: isLast ? 'none' : '1px dashed rgba(0,0,0,0.06)' }}>
      <span style={{ fontWeight:600, color:'#1a1208' }}>
        {item.name}{item.variant === 'half' && <HalfBadge/>}
        {item.note && <span style={{ fontSize:10, color:'#8c7355', marginLeft:5, fontStyle:'italic' }}>· {item.note}</span>}
        <span style={{ color:'#8c7355', marginLeft:6 }}>×{item.qty}</span>
      </span>
      <span style={{ fontWeight:700, color:'#ff8c00', flexShrink:0 }}>₹{(parseFloat(item.price) * parseInt(item.qty)).toFixed(0)}</span>
    </div>
  );
}

function Subtotal({ label, amount }) {
  return (
    <div style={{ marginTop:8, paddingTop:8, borderTop:'1px dashed rgba(0,0,0,0.08)', display:'flex', justifyContent:'space-between', fontSize:13, fontWeight:800 }}>
      <span style={{ color:'#8c7355' }}>{label}</span>
      <span style={{ color:'#1a1208' }}>₹{amount.toFixed(0)}</span>
    </div>
  );
}

function Page({ children, title, name, back, backLabel, right }) {
  return (
    <div style={{ background:'#faf7f2', minHeight:'100vh' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 14px', background:'#fff', borderBottom:'1px solid rgba(0,0,0,0.06)', position:'sticky', top:0, zIndex:50 }}>
        {back && <button onClick={back} style={{ border:'none', background:'rgba(0,0,0,0.06)', borderRadius:8, padding:'5px 10px', cursor:'pointer', fontSize:11, fontWeight:700, color:'#8c7355', display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>← {backLabel}</button>}
        <h2 style={{ fontSize:14, fontWeight:800, color:'#1a1208', margin:0, flex:1 }}>{title}</h2>
        {name && !back && <span style={{ fontSize:11, color:'#8c7355' }}>{name}</span>}
        {right}
      </div>
      {children}<KF/>
    </div>
  );
}

function Splash({ children }) {
  return <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', gap:8, padding:20, textAlign:'center' }}>{children}<KF/></div>;
}

const cardStyle  = border => ({ background:'#fff', borderRadius:14, overflow:'hidden', border:`1.5px solid ${border}`, boxShadow:'0 2px 10px rgba(0,0,0,0.05)' });
const cardHeader = bg     => ({ background: bg, padding:'9px 14px', display:'flex', alignItems:'center', justifyContent:'space-between' });
const HalfBadge  = ()    => <span style={{ fontSize:10, color:'#f59e0b', fontWeight:700, marginLeft:3 }}>½</span>;

const Field = ({ icon, placeholder, value, onChange, type = 'text' }) => (
  <div style={{ display:'flex', alignItems:'center', gap:10, background:'#fff', border:'1.5px solid rgba(0,0,0,0.09)', borderRadius:10, padding:'10px 14px' }}>
    {React.cloneElement(icon, { style:{ color:'#c4b49e', flexShrink:0 } })}
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type}
      style={{ border:'none', background:'transparent', outline:'none', fontSize:13, color:'#1a1208', flex:1 }}/>
  </div>
);

function KF() {
  return <style>{`@keyframes spin{to{transform:rotate(360deg);}}*{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;-webkit-font-smoothing:antialiased;}`}</style>;
}

const S = {
  center:  { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'80vh', gap:8, color:'#8c7355', padding:20, textAlign:'center' },
  primary: { width:'100%', padding:'13px 16px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#ff8c00,#ffbf00)', color:'#fff', fontSize:14, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 },
  ghost:   { border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', padding:3, color:'#8c7355' },
};
